/**
 * TikTok Pixel Analytics
 * GDPR-compliant implementation with consent management and TTCLID tracking
 */

const TIKTOK_PIXEL_ID = "D51SGBRC77UEA6JI8EAG"

/**
 * Capture and store TTCLID from URL
 * TikTok Click ID is used for attribution
 */
export const captureTTCLID = () => {
  if (typeof window === "undefined") return

  try {
    const urlParams = new URLSearchParams(window.location.search)
    const ttclid = urlParams.get("ttclid")

    if (ttclid) {
      // Store TTCLID in localStorage for 30 days
      const expiryDate = new Date()
      expiryDate.setDate(expiryDate.getDate() + 30)

      localStorage.setItem(
        "ttclid",
        JSON.stringify({
          value: ttclid,
          expiry: expiryDate.getTime(),
        })
      )

      console.log("📍 TTCLID captured:", ttclid)
    }
  } catch (e) {
    console.error("Failed to capture TTCLID:", e)
  }
}

/**
 * Get stored TTCLID if not expired
 */
export const getTTCLID = () => {
  if (typeof window === "undefined") return null

  try {
    const stored = localStorage.getItem("ttclid")
    if (!stored) return null

    const { value, expiry } = JSON.parse(stored)

    // Check if expired
    if (new Date().getTime() > expiry) {
      localStorage.removeItem("ttclid")
      return null
    }

    return value
  } catch (e) {
    console.error("Failed to get TTCLID:", e)
    return null
  }
}

/**
 * Initialize TikTok Pixel
 * This should be called ONLY after marketing consent is granted
 */
export const initTikTokPixel = () => {
  if (typeof window === "undefined") return

  // Check if already initialized
  if (window.ttq) {
    console.log("✅ TikTok Pixel already initialized")
    return
  }

  console.log("🚀 Initializing TikTok Pixel...")

  // Initialize TikTok Analytics Object
  window.TiktokAnalyticsObject = "ttq"
  const ttq = (window.ttq = window.ttq || [])

  // Define methods
  ttq.methods = [
    "page",
    "track",
    "identify",
    "instances",
    "debug",
    "on",
    "off",
    "once",
    "ready",
    "alias",
    "group",
    "enableCookie",
    "disableCookie",
    "holdConsent",
    "revokeConsent",
    "grantConsent",
  ]

  // Helper function to defer method calls
  ttq.setAndDefer = function (t, e) {
    t[e] = function () {
      t.push([e].concat(Array.prototype.slice.call(arguments, 0)))
    }
  }

  // Set up all methods
  for (let i = 0; i < ttq.methods.length; i++) {
    ttq.setAndDefer(ttq, ttq.methods[i])
  }

  // Instance helper
  ttq.instance = function (t) {
    const instanceArray = ttq._i[t] || []
    for (let n = 0; n < ttq.methods.length; n++) {
      ttq.setAndDefer(instanceArray, ttq.methods[n])
    }
    return instanceArray
  }

  // Load script
  ttq.load = function (pixelId, options) {
    const scriptUrl = "https://analytics.tiktok.com/i18n/pixel/events.js"
    ttq._i = ttq._i || {}
    ttq._i[pixelId] = []
    ttq._i[pixelId]._u = scriptUrl
    ttq._t = ttq._t || {}
    ttq._t[pixelId] = +new Date()
    ttq._o = ttq._o || {}
    ttq._o[pixelId] = options || {}

    const script = document.createElement("script")
    script.type = "text/javascript"
    script.async = true
    script.src = scriptUrl + "?sdkid=" + pixelId + "&lib=ttq"

    const firstScript = document.getElementsByTagName("script")[0]
    firstScript.parentNode.insertBefore(script, firstScript)
  }

  // Load TikTok Pixel
  ttq.load(TIKTOK_PIXEL_ID)

  // Track initial page view
  ttq.page()

  console.log("✅ TikTok Pixel initialized successfully")
}

/**
 * Check if marketing consent has been granted
 */
export const hasMarketingConsent = () => {
  if (typeof window === "undefined") return false

  try {
    const consent = localStorage.getItem("cookie-consent")
    if (!consent) return false

    const parsed = JSON.parse(consent)
    return parsed.marketing === true
  } catch (e) {
    return false
  }
}

/**
 * Track a custom event
 * Only tracks if consent is granted and pixel is initialized
 * @param {string} eventName - Name of the event
 * @param {object} properties - Event properties
 */
export const trackTikTokEvent = (eventName, properties = {}) => {
  if (typeof window === "undefined") return

  // Check consent
  if (!hasMarketingConsent()) {
    console.log("⚠️ TikTok tracking blocked: No marketing consent")
    return
  }

  // Check if pixel is initialized
  if (!window.ttq) {
    console.warn("⚠️ TikTok Pixel not initialized")
    return
  }

  // Add TTCLID to properties if available
  const ttclid = getTTCLID()
  if (ttclid) {
    properties.ttclid = ttclid
  }

  window.ttq.track(eventName, properties)
  console.log(`📊 TikTok Event: ${eventName}`, properties)
}

/**
 * Track page view
 * Only tracks if consent is granted and pixel is initialized
 */
export const trackTikTokPageView = () => {
  if (typeof window === "undefined") return

  // Check consent
  if (!hasMarketingConsent()) {
    console.log("⚠️ TikTok page view blocked: No marketing consent")
    return
  }

  // Check if pixel is initialized
  if (!window.ttq) {
    console.warn("⚠️ TikTok Pixel not initialized")
    return
  }

  window.ttq.page()
  console.log("📄 TikTok Page View tracked")
}
